import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Package,
  Zap,
  Loader2,
  ChevronRight,
  ChevronDown,
  Ban,
  RotateCcw,
  Eye,
  Copy,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  fetchContainerOrders,
  fetchContainerOrderColumns,
  cancelContainerOrder,
  fetchCancelPreview,
  reopenContainerOrder,
  generateContainerLines,
  getContainerOrderCopyData,
} from "../../store/slices/containerOrdersSlice";
import Button from "../../components/UI/Button";
import { useConfirm } from "../../hooks/useConfirm";
import ActionMenu from "../../components/UI/ActionMenu";
import { Input } from "../../components/UI/FormField";
import { Pagination } from "../../components/ListView/Pagination";
import { GenerateLinesModal } from "../../components/ContainerOrderForm/GenerateLinesModal";
import { OrderLinesPanel } from "../../components/ContainerOrderForm/OrderLinesPanel";
import {
  ContainerOrderFiltersComponent,
  type ContainerOrderFacet,
} from "../../components/containerOrders/ContainerOrderFilters";
import ContainerOrderColumnConfig from "../../components/containerOrders/ContainerOrderColumnConfig";
import {
  containerOrderColumnLabelKey,
  visibleContainerOrderColumns,
} from "../../utils/containerOrderColumns";
import { orderedRouteStops } from "../../utils/containerOrderRoute";
import HandlerAssign from "../../components/handler/HandlerAssign";
import HandlerScopeFilter from "../../components/handler/HandlerScopeFilter";
import { useHandlerScope } from "../../hooks/useHandlerScope";
import { containerOrderAPI } from "../../services/api";
import RouteChain from "../../components/containerOrders/RouteChain";
import { PartnerColorDot } from "../../components/partners/PartnerColorPicker";
import { partnerStripe } from "../../constants/partnerColors";
import type { Column, PartnerSuggestion } from "../../types";
import type { ContainerOrderCancelPreview } from "../../types/containerOrders";
import type {
  ContainerOrder,
  ContainerOrderFilters,
  GenerateLinesPayload,
} from "../../types/containerOrders";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const EMPTY_FILTERS: ContainerOrderFilters = {
  page: 1,
  limit: PAGE_SIZE,
  search: "",
  containerTypes: [],
  cutOffFrom: "",
  cutOffTo: "",
  withoutLines: false,
};

/** One applied filter, removable in place. */
function FilterChip({
  prefix,
  label,
  onRemove,
}: {
  prefix?: string;
  label: string;
  onRemove: () => void;
}) {
  const { t } = useTranslation("containerOrders");

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 py-1 pl-2.5 pr-1.5 text-[11px] text-secondary-700">
      {prefix && <span className="text-secondary-500">{prefix}</span>}
      <span className="font-medium">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("filters.removeFilter", {
          name: prefix ? `${prefix} ${label}` : label,
        })}
        className="text-secondary-400 transition-colors hover:text-ink"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

export default function ContainerOrdersList() {
  const { t } = useTranslation(["containerOrders", "common"]);
  const { confirm, confirmDialog } = useConfirm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    orders,
    pagination,
    loading,
    generating,
    containerMatchOrderIds,
    columns,
    columnsLoaded,
  } = useAppSelector((state) => state.containerOrders);

  // ── Column settings ───────────────────────────────────────────────────────
  // Dispatched unconditionally; the thunk itself declines when the answer is
  // already in the store, so a re-mount — React's double-mount in development,
  // or simply coming back to the list — costs nothing.
  useEffect(() => {
    dispatch(fetchContainerOrderColumns());
  }, [dispatch]);

  /** The columns actually drawn, left to right. */
  const visibleColumns = useMemo(
    () => visibleContainerOrderColumns(columns),
    [columns],
  );

  /**
   * Columns plus the one control that is not a column — the expand chevron —
   * for the rows that span the whole table.
   */
  const fullSpan = visibleColumns.length + 1;

  const [filters, setFilters] = useState<ContainerOrderFilters>(EMPTY_FILTERS);
  // Directory picks are kept as records, not names: the chips and the panel
  // need something stable to key on, and only the request wants plain names.
  const [selectedClients, setSelectedClients] = useState<PartnerSuggestion[]>(
    [],
  );
  const [selectedTerminals, setSelectedTerminals] = useState<
    PartnerSuggestion[]
  >([]);
  const [searchInput, setSearchInput] = useState("");

  /**
   * Mine / Everyone / a person, remembered for this screen.
   *
   * Kept outside `filters` even though it goes into the same request. The
   * filter panel owns `filters` — it counts its facets into a badge and clears
   * them with its Clear filters button — and the handler scope is neither one
   * of its facets nor something that button should silently reset. It has its
   * own control, its own visible state, and its own memory.
   */
  const { scope: handlerScope, setScope: setHandlerScope, param: handlerParam } =
    useHandlerScope("orders");

  const [generateFor, setGenerateFor] = useState<ContainerOrder | null>(null);
  // Expanded order rows, and a per-order token bumped after generating so the
  // open panel refetches instead of showing a stale batch.
  const [expanded, setExpanded] = useState<string[]>([]);
  // Rows the search opened by itself, kept apart from the ones the user opened
  // so that clearing the search puts the list back the way it was found —
  // without also closing anything the user had opened for their own reasons.
  const [autoExpanded, setAutoExpanded] = useState<string[]>([]);
  const [refreshTokens, setRefreshTokens] = useState<Record<string, number>>(
    {},
  );

  const isExpanded = (id: string) =>
    expanded.includes(id) || autoExpanded.includes(id);

  /**
   * Close these rows and only these rows.
   *
   * Takes a set rather than one id so it is impossible to express "close
   * everything". Every open panel belongs to somebody's train of thought, and an
   * action on one order has no business collapsing the order above it.
   */
  const collapseRows = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const closing = new Set(ids);
    setExpanded((prev) => prev.filter((e) => !closing.has(e)));
    setAutoExpanded((prev) => prev.filter((e) => !closing.has(e)));
  }, []);

  const toggleExpanded = (id: string) => {
    if (!isExpanded(id)) {
      setExpanded((prev) => [...prev, id]);
      return;
    }
    // Closing clears both lists, or a row the search opened would spring
    // straight back open under the user's own click.
    collapseRows([id]);
  };

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Returning `prev` unchanged matters: the timer also fires on mount, and
      // a new object there would cost a second identical request.
      setFilters((prev) =>
        prev.search === searchInput
          ? prev
          : { ...prev, search: searchInput, page: 1 },
      );
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /** The search actually applied to the list — not the box mid-keystroke. */
  const activeSearch = filters.search?.trim() ?? "";

  /**
   * Which rows on this page were found through one of their containers.
   *
   * Joined into a key so the effect below reacts to the *set* changing rather
   * than to the array identity: the list refetches after every mutation, and a
   * fresh array each time would re-open rows the user had just closed.
   */
  const containerMatchKey = useMemo(
    () => containerMatchOrderIds.join(","),
    [containerMatchOrderIds],
  );

  const containerMatchSet = useMemo(
    () => new Set(containerMatchOrderIds),
    [containerMatchOrderIds],
  );

  // An order matched by a container number is, on the face of the row, a result
  // with nothing to do with what was typed — the box is one level down. So the
  // list opens those rows itself, and their panel is given the same search so
  // it shows the container that matched instead of the whole batch.
  useEffect(() => {
    setAutoExpanded(
      activeSearch && containerMatchKey ? containerMatchKey.split(",") : [],
    );
  }, [activeSearch, containerMatchKey]);

  /**
   * The request the current filter state describes.
   *
   * Built in one place rather than at each dispatch site: empty facets are
   * dropped instead of being sent as blanks, and the two directory pickers are
   * flattened to the names the API matches on.
   */
  const queryParams = useMemo<ContainerOrderFilters>(() => {
    const params: ContainerOrderFilters = {
      page: filters.page,
      limit: filters.limit,
    };

    if (filters.search?.trim()) params.search = filters.search.trim();
    if (filters.containerTypes?.length)
      params.containerTypes = filters.containerTypes;
    if (filters.cutOffFrom) params.cutOffFrom = filters.cutOffFrom;
    if (filters.cutOffTo) params.cutOffTo = filters.cutOffTo;
    if (filters.withoutLines) params.withoutLines = true;
    if (handlerParam) params.handler = handlerParam;

    if (selectedClients.length) {
      params.clients = selectedClients.map((c) => c.companyName.trim());
    }
    if (selectedTerminals.length) {
      params.terminals = selectedTerminals.map((t) => t.companyName.trim());
    }

    return params;
  }, [filters, selectedClients, selectedTerminals, handlerParam]);

  useEffect(() => {
    dispatch(fetchContainerOrders(queryParams));
  }, [dispatch, queryParams]);

  // Deleting the last rows of the last page strands the user on a page that no
  // longer exists. Step back to the final real page rather than showing an
  // empty table under a pager that claims there is more.
  useEffect(() => {
    if (loading) return;
    if (pagination.pages > 0 && pagination.page > pagination.pages) {
      setFilters((prev) => ({ ...prev, page: pagination.pages }));
    }
  }, [loading, pagination.page, pagination.pages]);

  /** Re-read the current page after a mutation changed it. */
  const refreshList = () => {
    dispatch(fetchContainerOrders(queryParams));
  };

  const paginationDetails = useMemo(
    () => ({
      page: pagination.page,
      pages: pagination.pages,
      total: pagination.total,
      hasNext: pagination.page < pagination.pages,
      hasPrev: pagination.page > 1,
    }),
    [pagination],
  );

  // ── Filter handlers ───────────────────────────────────────────────────────
  // Any change to what is being filtered puts the list back on page 1: the
  // page the user was on rarely exists in the narrower result set.
  const handleFiltersChange = (patch: Partial<ContainerOrderFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  /** Add or remove one value from a multi-select facet. */
  const handleToggleFacet = (facet: ContainerOrderFacet, value: string) =>
    setFilters((prev) => {
      const current = (prev[facet] ?? []) as string[];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [facet]: next, page: 1 };
    });

  const handleClientSelect = (client: PartnerSuggestion) => {
    setSelectedClients((prev) =>
      prev.some((c) => c._id === client._id) ? prev : [...prev, client],
    );
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleClientRemove = (clientId: string) => {
    setSelectedClients((prev) => prev.filter((c) => c._id !== clientId));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTerminalSelect = (terminal: PartnerSuggestion) => {
    setSelectedTerminals((prev) =>
      prev.some((c) => c._id === terminal._id) ? prev : [...prev, terminal],
    );
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTerminalRemove = (terminalId: string) => {
    setSelectedTerminals((prev) => prev.filter((c) => c._id !== terminalId));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  /** How many filters are applied — the panel badge and the chip row. */
  const activeFilterCount =
    (filters.containerTypes?.length ?? 0) +
    selectedClients.length +
    selectedTerminals.length +
    (filters.cutOffFrom || filters.cutOffTo ? 1 : 0) +
    (filters.withoutLines ? 1 : 0);

  // Clears the search box too, even though it lives outside the panel.
  const clearFilters = () => {
    setSearchInput("");
    setSelectedClients([]);
    setSelectedTerminals([]);
    setFilters(EMPTY_FILTERS);
  };

  /**
   * The order's cancel question, with the server's own figures in it.
   *
   * Fetched rather than assembled here, and that is not fussiness: this list
   * holds one page of a batch that may run to hundreds of containers, so every
   * number that matters — how many will actually be closed, what they are
   * carrying in un-invoiced charges, how many board slots come back — is a
   * question only the server can answer.
   *
   * The charge total is the one that earns the round trip. Cancelling means not
   * chargeable, so those charges stop being billable the instant this goes
   * through, permanently, with no screen that would afterwards show what was
   * given up. A dialog that asked "are you sure?" over that would be a
   * formality; this one states the cost.
   *
   * A failed preview does not block the action — the server will apply the same
   * rules either way — it just asks the plainer question. Refusing to let
   * somebody cancel an order because a summary endpoint was slow would be the
   * wrong trade.
   */
  const handleCancelOne = async (order: ContainerOrder) => {
    let preview: ContainerOrderCancelPreview | null = null;
    try {
      preview = await dispatch(fetchCancelPreview(order._id)).unwrap();
    } catch {
      preview = null;
    }

    // The server re-checks anyway; this just saves the user a pointless dialog
    // when it already knows the answer.
    if (preview?.blocked) {
      toast.error(preview.blocked);
      return;
    }

    const agreed = await confirm({
      title: t("confirm.titles.cancelOrder"),
      description: preview ? (
        <div className="space-y-2">
          <p>{t("confirm.cancelOrder")}</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>
              {t("confirm.cancelOrderBody.containers", {
                count: preview.containers,
              })}
            </li>
            {preview.releasedCells > 0 && (
              <li>
                {t("confirm.cancelOrderBody.slots", {
                  count: preview.releasedCells,
                })}
              </li>
            )}
            <li>
              {preview.chargeCount > 0
                ? t("confirm.cancelOrderBody.charges", {
                    count: preview.chargeCount,
                    amount: preview.chargeTotal.toFixed(2),
                    currency: preview.currency,
                  })
                : t("confirm.cancelOrderBody.noCharges")}
            </li>
            <li>{t("confirm.cancelOrderBody.reopen")}</li>
          </ul>
        </div>
      ) : (
        t("confirm.cancelOrder")
      ),
      confirmLabel: t("confirm.actions.cancelOrder"),
      tone: "warning",
    });
    if (!agreed) return;
    try {
      await dispatch(
        cancelContainerOrder({ id: order._id, updatedAt: order.updatedAt }),
      ).unwrap();
      toast.success(t("toasts.cancelled"));
      /**
       * This order's panel closes; nobody else's moves.
       *
       * What it was showing is gone — every container in it has just been closed
       * — and leaving it open presents a batch of cancelled rows under a header
       * that now reads zero. Closing is the honest outcome, and the expand
       * control is still there to open it again (see `hasContainers`).
       */
      collapseRows([order._id]);
      refreshList();
    } catch (error: any) {
      toast.error(
        typeof error === "string" ? error : t("toasts.statusChangeFailed"),
      );
    }
  };

  const handleReopenOne = async (order: ContainerOrder) => {
    const agreed = await confirm({
      title: t("confirm.titles.reopenOrder"),
      description: t("confirm.reopenOrder"),
      confirmLabel: t("confirm.actions.reopenOrder"),
      tone: "neutral",
    });
    if (!agreed) return;
    try {
      await dispatch(
        reopenContainerOrder({ id: order._id, updatedAt: order.updatedAt }),
      ).unwrap();
      toast.success(t("toasts.reopened"));
      refreshList();
    } catch (error: any) {
      toast.error(
        typeof error === "string" ? error : t("toasts.statusChangeFailed"),
      );
    }
  };

  /**
   * Start a new order from an existing one.
   *
   * Nothing is created here: the copy opens in the create form, unsaved, so it
   * is read and completed before it exists. What arrives is the order alone —
   * its containers are not copied, and the two fields that name one booking
   * (Reference, Booking number) and its documents come back empty for this
   * order to fill in.
   */
  const handleCopyOne = async (order: ContainerOrder) => {
    try {
      const { order: copyData, sourceOrder } = await dispatch(
        getContainerOrderCopyData(order._id),
      ).unwrap();
      navigate("/container-orders/new", { state: { copyData, sourceOrder } });
    } catch (error: any) {
      toast.error(typeof error === "string" ? error : t("toasts.copyFailed"));
    }
  };

  const handleGenerate = async (payload: GenerateLinesPayload) => {
    if (!generateFor) return;

    try {
      const orderId = generateFor._id;
      const result = await dispatch(
        generateContainerLines({ id: orderId, payload }),
      ).unwrap();
      toast.success(t("toasts.generated", { count: result.lines.length }));
      setGenerateFor(null);
      // Reveal what was just created, and force the panel to refetch.
      setExpanded((prev) =>
        prev.includes(orderId) ? prev : [...prev, orderId],
      );
      setRefreshTokens((prev) => ({
        ...prev,
        [orderId]: (prev[orderId] ?? 0) + 1,
      }));
      refreshList();
    } catch (error: any) {
      toast.error(
        typeof error === "string" ? error : t("toasts.generateFailed"),
      );
    }
  };

  const th =
    "px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-secondary-600";
  // `align-top` because the route cell can run to several lines: with the
  // other cells centred against it, a tall row reads as if its values had come
  // loose from each other.
  const td = "px-3 py-2 align-top text-[12px] text-gray-800";

  /** The dash a cell shows when it has nothing to show. */
  const blank = <span className="text-gray-400">—</span>;

  /** A date as the operator's locale writes it, or nothing at all. */
  const asDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : null;

  /**
   * One cell, chosen by column id.
   *
   * A switch over ids rather than a table of render functions kept beside the
   * defaults, because every branch below reaches for something in this
   * component's scope — the row's handlers, the translation function, the
   * navigate. The ids it answers to are exactly the ones
   * `containerOrderColumns.ts` lists, and the list filters anything else out
   * before it gets here, so an unknown id cannot leave a header standing over
   * a missing cell.
   */
  const renderCell = (column: Column, order: ContainerOrder) => {
    switch (column.id) {
      case "orderNumber":
        return (
          <td
            className={`${td} whitespace-nowrap font-mono font-bold text-ink`}
          >
            {order.orderNumber}
          </td>
        );

      // The client's own reference — what they quote back at us on the phone,
      // so it earns a column of its own rather than sitting as a footnote
      // under the order number.
      case "referenceNumber":
        return (
          <td className={`${td} whitespace-nowrap font-mono text-[11px]`}>
            {order.referenceNumber || blank}
          </td>
        );

      // The carrier's booking number — stored as `contractNumber` for
      // historical reasons, labelled "Booking no." everywhere it is shown.
      case "contractNumber":
        return (
          <td className={`${td} whitespace-nowrap font-mono text-[11px]`}>
            {order.contractNumber || blank}
          </td>
        );

      case "client": {
        // The dot repeats the row's stripe beside the name it stands for.
        const stripe = partnerStripe(order.clientPartner);
        return (
          <td className={td}>
            {order.client?.companyName ? (
              <span className="inline-flex items-center gap-1.5">
                <PartnerColorDot color={stripe} />
                {order.client.companyName}
              </span>
            ) : (
              blank
            )}
          </td>
        );
      }

      // Initials, as the shipments list does it: a full name doubles the width
      // of a dense table to say something a two-letter monogram already says.
      case "createdBy": {
        const by = order.createdBy;
        const initials = by
          ? `${by.firstName?.[0] ?? ""}${by.lastName?.[0] ?? ""}`.toUpperCase()
          : "";
        return (
          <td className={`${td} whitespace-nowrap`}>
            {initials ? (
              <span
                title={
                  `${by?.firstName ?? ""} ${by?.lastName ?? ""}`.trim() ||
                  undefined
                }
              >
                {initials}
              </span>
            ) : (
              blank
            )}
          </td>
        );
      }

      /**
       * Who is dealing with this booking, and the control that changes it.
       *
       * Pressable in the list rather than only on the order's own page, because
       * that is where the question comes up: a dispatcher scanning the morning's
       * orders hands three of them to colleagues without opening any. On an
       * unassigned row one press takes it.
       *
       * A cancelled order is *not* read-only here, unlike every other cell on
       * its row. Its containers may still carry unbilled charges somebody has to
       * see to, and when a dispatcher leaves it is exactly the closed records
       * that need a new owner — which is why the endpoint behind this refuses no
       * status. See `setOrderHandler`.
       */
      case "handler":
        return (
          <td
            className={`${td} whitespace-nowrap`}
            onClick={(e) => e.stopPropagation()}
          >
            <HandlerAssign
              handler={order.handlerId ?? null}
              label={t("handler.forOrder", { order: order.orderNumber })}
              onAssign={async (handlerId) => {
                const res = await containerOrderAPI.setOrderHandler(
                  order._id,
                  handlerId,
                );
                /**
                 * Refetch rather than patch the row in place.
                 *
                 * Under an active scope the change may mean the row no longer
                 * belongs on screen at all — handing your own order to a
                 * colleague while filtered to Mine should take it off the list,
                 * and the count in the pager with it. Only the server knows
                 * that, because the row may still match through one of its
                 * containers.
                 */
                refreshList();
                return res;
              }}
              onError={(message) => toast.error(message)}
            />
          </td>
        );

      case "incoterm":
        return (
          <td className={`${td} whitespace-nowrap`}>
            {order.incoterm ? (
              <span>
                <span className="font-semibold">{order.incoterm}</span>
                {order.place && (
                  <span className="text-gray-500"> {order.place}</span>
                )}
              </span>
            ) : (
              blank
            )}
          </td>
        );

      case "containerType":
        return (
          <td className={`${td} whitespace-nowrap font-mono`}>
            {order.containerType || blank}
          </td>
        );

      case "isoCode":
        return (
          <td className={`${td} whitespace-nowrap font-mono text-[11px]`}>
            {order.isoCode || blank}
          </td>
        );

      case "shippingLine":
        return (
          <td className={`${td} max-w-40`}>
            <div className="truncate">{order.shippingLine || blank}</div>
          </td>
        );

      // Vessel over voyage, the way the route column stacks its two terminals:
      // a voyage number without its ship is not something anyone looks up.
      case "vessel":
        return (
          <td className={`${td} max-w-40 text-[11px]`}>
            {order.vesselName || order.voyageNumber ? (
              <>
                <div className="truncate">{order.vesselName || "—"}</div>
                {order.voyageNumber && (
                  <div className="truncate text-gray-400">
                    {order.voyageNumber}
                  </div>
                )}
              </>
            ) : (
              blank
            )}
          </td>
        );

      // Badges, not text: these are flags an operator scans a column for
      // ("which of today's orders are ADR?"), never reads word by word.
      case "handling": {
        const h = order.handling;
        const flags: { key: string; label: string; className: string }[] = [];
        if (h?.temperatureSensitive) {
          flags.push({
            key: "temp",
            label: h.temperatureRange || t("handling.temperature"),
            className: "bg-sky-100 text-sky-700",
          });
        }
        if (h?.adr) {
          flags.push({
            key: "adr",
            label: h.imoClass ? `ADR ${h.imoClass}` : "ADR",
            className: "bg-amber-100 text-amber-700",
          });
        }
        if (h?.fragile) {
          flags.push({
            key: "fragile",
            label: t("handling.fragile"),
            className: "bg-rose-100 text-rose-700",
          });
        }
        return (
          <td className={td}>
            {flags.length ? (
              <div className="flex flex-wrap gap-1">
                {flags.map((flag) => (
                  <span
                    key={flag.key}
                    className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold ${flag.className}`}
                  >
                    {flag.label}
                  </span>
                ))}
              </div>
            ) : (
              blank
            )}
          </td>
        );
      }

      // Terminal names run long; cap the column so one order cannot stretch
      // the whole table sideways.
      /**
       * The route the order actually describes, derived rather than assumed.
       *
       * This used to print `terminalPickup → terminalDropoff`, which was three
       * different lies: it hid every stop added between the anchors, it showed
       * a terminal that had been switched off as though a truck were going
       * there, and it never showed where the box is loaded. `orderedRouteStops`
       * flattens the template the same way the server does when it generates a
       * container, so the row and the driver see one route.
       *
       * Shown as its two ends with a count of what lies between: the endpoints
       * are what a dispatcher scans for, and a route of six names does not fit
       * a cell. The full chain is on the cell's tooltip, and on the order.
       */
      case "route":
        return (
          <td className={`${td} text-[11px]`}>
            <RouteChain
              stops={orderedRouteStops(order.route)}
              fallback={blank}
              className="max-w-80"
            />
          </td>
        );

      // Stop 2 — where the box is actually loaded. Capped for the same reason
      // as the terminals beside it.
      case "loadingSite":
        return (
          <td className={`${td} max-w-45 text-[11px]`}>
            <div className="truncate">
              {order.route?.loadingSite?.companyName || blank}
            </div>
            {order.route?.loadingSite?.address?.city && (
              <div className="truncate text-gray-400">
                {order.route.loadingSite.address.city}
              </div>
            )}
          </td>
        );

      case "cutOff": {
        const cutOff = order.route?.terminalDropoff;
        return (
          <td className={`${td} whitespace-nowrap text-[11px]`}>
            {cutOff?.cutOffDate ? (
              <>
                <div>{new Date(cutOff.cutOffDate).toLocaleDateString()}</div>
                {cutOff.cutOffTime && (
                  <div className="text-gray-400">{cutOff.cutOffTime}</div>
                )}
              </>
            ) : (
              blank
            )}
          </td>
        );
      }

      // The batch's validity window. One column: a "from" without its "to" is
      // half a date range, and neither half is worth a column of its own.
      case "validity": {
        const from = asDate(order.dateFrom);
        const to = asDate(order.dateTo);
        return (
          <td className={`${td} whitespace-nowrap text-[11px]`}>
            {from || to ? (
              <>
                <div>{from || "—"}</div>
                <div className="text-gray-400">→ {to || "—"}</div>
              </>
            ) : (
              blank
            )}
          </td>
        );
      }

      case "containers": {
        const generated = order.lineProgress?.total ?? 0;
        // Keyed by the status value itself, which is lower-case ("completed"),
        // exactly as the expanded panel's progress bar reads it. `Completed`
        // matched nothing and always read 0.
        const completed = order.lineProgress?.byStatus?.completed ?? 0;
        return (
          <td className={td}>
            {generated > 0 ? (
              <span className="whitespace-nowrap rounded bg-primary-50 px-1.5 py-0.5 text-[11px] font-semibold text-primary-700">
                {completed}/{generated}
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">
                {t("list.noLines")}
              </span>
            )}
          </td>
        );
      }

      // A count, not a list: the row says whether there is anything filed, and
      // the order's own page is where they are read.
      case "documents": {
        const count = order.attachments?.length ?? 0;
        return (
          <td className={`${td} whitespace-nowrap`}>
            {count > 0 ? (
              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] font-semibold text-secondary-700">
                {count}
              </span>
            ) : (
              blank
            )}
          </td>
        );
      }

      // Capped and truncated — notes run to paragraphs, and one long note
      // must not set the height of every row on the page.
      case "notes":
        return (
          <td className={`${td} max-w-60 text-[11px]`}>
            {order.additionalNotes ? (
              <div className="truncate" title={order.additionalNotes}>
                {order.additionalNotes}
              </div>
            ) : (
              blank
            )}
          </td>
        );

      case "createdAt":
        return (
          <td className={`${td} whitespace-nowrap text-[11px]`}>
            {asDate(order.createdAt) || blank}
          </td>
        );

      case "updatedAt":
        return (
          <td className={`${td} whitespace-nowrap text-[11px]`}>
            {asDate(order.updatedAt) || blank}
          </td>
        );

      // Every row action lives behind one menu: it keeps the column narrow on
      // a table that is already wide, and it gives each action room for a
      // label and a reason when it is unavailable. The menu portals itself and
      // clamps to the viewport, so it cannot be clipped by the table's
      // horizontal scroll or run off a small screen.
      case "actions":
        return (
          <td className={`${td} w-12`} onClick={(e) => e.stopPropagation()}>
            <ActionMenu
              ariaLabel={t("list.columns.actions")}
              // A bordered button rather than a bare glyph: three floating dots
              // in a dense table read as decoration.
              buttonClassName="inline-flex items-center justify-center rounded-md border border-[rgba(11,15,14,0.15)] bg-white p-1.5 text-secondary-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              items={[
                {
                  label: t("list.openOrder"),
                  icon: <Eye className="h-4 w-4" />,
                  onClick: () => navigate(`/container-orders/${order._id}`),
                },
                {
                  label: t("list.addContainers"),
                  icon: <Zap className="h-4 w-4" />,
                  onClick: () => setGenerateFor(order),
                  disabled: order.status === "cancelled",
                  title:
                    order.status === "cancelled"
                      ? t("hints.cancelledReadonly")
                      : undefined,
                },
                {
                  label: t("actions.copyOrder"),
                  icon: <Copy className="h-4 w-4" />,
                  onClick: () => handleCopyOne(order),
                  // Offered on a cancelled order too: reusing a booking that
                  // fell through is one of the reasons to copy one at all, and
                  // the copy is a fresh draft, not a change to this record.
                },
                {
                  label: t("actions.cancelOrder"),
                  icon: <Ban className="h-4 w-4" />,
                  onClick: () => handleCancelOne(order),
                  hidden: order.status === "cancelled",
                  disabled: !!order.cancelBlockedReason,
                  // The server's own explanation, verbatim.
                  title: order.cancelBlockedReason ?? undefined,
                  divider: true,
                },
                {
                  label: t("actions.reopenOrder"),
                  icon: <RotateCcw className="h-4 w-4" />,
                  onClick: () => handleReopenOne(order),
                  hidden: order.status !== "cancelled",
                  divider: true,
                },
              ]}
            />
          </td>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mx-auto w-full p-3 sm:p-4">
      {/* ── Header ─────────────────────────────────────────────────────────
          Title and the primary action sit on one line; the selection actions
          get a band of their own below, so they can appear and disappear
          without the header reflowing around them. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <Package className="h-5 w-5 shrink-0 text-primary" />
          <h1 className="truncate text-[16px] font-bold text-ink">
            {t("list.title")}
          </h1>
          <span className="shrink-0 rounded bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-700">
            {pagination.total}
          </span>
          {/* A refresh over rows that are already on screen. It says so here
              rather than by emptying the table — see the tbody below. */}
          {loading && orders.length > 0 && (
            <Loader2
              className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400"
              aria-label={t("common:labels.loading")}
            />
          )}
        </div>

        <Button
          className="shrink-0"
          onClick={() => navigate("/container-orders/new")}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {t("list.newOrder")}
        </Button>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────
          Search stays on the surface because it is the one control used on
          almost every visit; everything else lives in the panel, where facets
          can be multi-select without a row of dropdowns eating the page. */}
      <div className="mb-3 rounded-md border border-[rgba(11,15,14,0.08)] bg-white p-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-64">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mist" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("list.searchPlaceholder")}
              size="sm"
              className="pl-7"
            />
          </div>

          <ContainerOrderFiltersComponent
            filters={filters}
            selectedClients={selectedClients}
            selectedTerminals={selectedTerminals}
            activeCount={activeFilterCount}
            onChange={handleFiltersChange}
            onToggle={handleToggleFacet}
            onClientSelect={handleClientSelect}
            onClientRemove={handleClientRemove}
            onTerminalSelect={handleTerminalSelect}
            onTerminalRemove={handleTerminalRemove}
            onClearFilters={clearFilters}
          />

          {/* Beside the filter panel rather than inside it: two of its three
              answers are one press, which a dropdown would take away, and its
              current state has to be readable without opening anything. */}
          <HandlerScopeFilter
            scope={handlerScope}
            onChange={(next) => {
              setHandlerScope(next);
              // Narrowing rarely leaves the page you were on in existence.
              setFilters((prev) => ({ ...prev, page: 1 }));
            }}
          />

          <ContainerOrderColumnConfig ready={columnsLoaded} />
        </div>
      </div>

      {/* ── Active filter chips ────────────────────────────────────────────
          What is applied, visible without reopening the panel, and each one
          removable where it is read. */}
      {activeFilterCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {(filters.containerTypes ?? []).map((type) => (
            <FilterChip
              key={`type-${type}`}
              label={type}
              onRemove={() => handleToggleFacet("containerTypes", type)}
            />
          ))}

          {selectedClients.map((client) => (
            <FilterChip
              key={`client-${client._id}`}
              prefix={t("filters.client")}
              label={client.companyName}
              onRemove={() => handleClientRemove(client._id)}
            />
          ))}

          {selectedTerminals.map((terminal) => (
            <FilterChip
              key={`terminal-${terminal._id}`}
              prefix={t("filters.terminal")}
              label={terminal.companyName}
              onRemove={() => handleTerminalRemove(terminal._id)}
            />
          ))}

          {(filters.cutOffFrom || filters.cutOffTo) && (
            <FilterChip
              prefix={t("filters.cutOff")}
              label={`${filters.cutOffFrom || "…"} → ${filters.cutOffTo || "…"}`}
              onRemove={() =>
                handleFiltersChange({ cutOffFrom: "", cutOffTo: "" })
              }
            />
          )}

          {filters.withoutLines && (
            <FilterChip
              label={t("filters.withoutLines")}
              onRemove={() => handleFiltersChange({ withoutLines: false })}
            />
          )}
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────────
          The header carries a checkbox, which made it read as just another
          data row. It is now visually separated from the body: a darker
          surface, a heavier bottom rule, and sticky so it stays the reference
          point while the list scrolls.

          Nothing is drawn until the column settings have arrived. Drawing the
          default layout first and correcting it a moment later is a visible
          rearrangement of the whole table — columns appearing, disappearing and
          sliding sideways — which reads as a glitch rather than as loading. It
          costs nothing to wait: the settings and the first page of orders are
          requested together, so the table appears when the slower of the two
          lands, and it appears already correct. Coming back to the list later
          skips this entirely, the settings being held for the session. */}
      <div className="overflow-x-auto rounded-md border border-[rgba(11,15,14,0.10)] bg-white">
        {!columnsLoaded ? (
          <div className="px-3 py-12 text-center">
            <Loader2
              className="mx-auto h-5 w-5 animate-spin text-gray-400"
              aria-label={t("common:labels.loading")}
            />
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="sticky top-0 z-10 border-b-2 border-[rgba(11,15,14,0.18)] bg-bone">
              <tr>
                <th className={`${th} w-8`} />
                {visibleColumns.map((column) => (
                  <th
                    key={column.id}
                    className={column.id === "actions" ? `${th} w-24` : th}
                  >
                    {t(containerOrderColumnLabelKey(column.id), {
                      defaultValue: column.name,
                    })}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {/* Only the first load replaces the table with a spinner. A refetch
                keeps the rows: rendering them on `!loading` meant every refresh
                unmounted every row — and with them every expanded containers
                panel, which then remounted and refetched its own containers.
                One edit cost three requests and reset the whole page. */}
              {loading && orders.length === 0 && (
                <tr>
                  <td colSpan={fullSpan} className="px-3 py-8 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                  </td>
                </tr>
              )}

              {!loading && orders.length === 0 && (
                <tr>
                  <td
                    colSpan={fullSpan}
                    className="px-3 py-10 text-center text-[12px] text-gray-500"
                  >
                    {t("list.empty")}
                  </td>
                </tr>
              )}

              {orders.map((order) => {
                /**
                 * Whether this order has containers to show at all.
                 *
                 * `total` is not that number: `countLines` keeps cancelled
                 * containers out of it on purpose, so an order whose whole batch
                 * was just cancelled reports `total: 0` while still holding
                 * every one of them. Reading it here made the expand control
                 * disappear the moment an order was cancelled — with the panel
                 * still open underneath and no way left to close it, because the
                 * only thing that closes it is the control that had just gone.
                 *
                 * The panel lists cancelled containers, so the control has to
                 * ask the same question the panel answers: are there any.
                 */
                const hasContainers =
                  (order.lineProgress?.total ?? 0) +
                    (order.lineProgress?.cancelled ?? 0) >
                  0;
                const rowExpanded = isExpanded(order._id);
                // Only rows the search reached through a container get the
                // filtered panel. An order matched on its own fields still
                // shows its whole batch — nothing there matched, and hiding
                // every container would read as an order with none.
                const rowSearch = containerMatchSet.has(order._id)
                  ? activeSearch
                  : undefined;
                // Whose order it is, at a glance: the client partner's colour
                // down the row's left edge. None for a typed-in client.
                const stripe = partnerStripe(order.clientPartner);

                return (
                  <Fragment key={order._id}>
                    <tr
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/container-orders/${order._id}`)}
                    >
                      {/* Expand — only meaningful once the order has children.
                          Also carries the partner stripe, as an inset shadow
                          so it takes no width from the table. */}
                      <td
                        className={`${td} w-8 align-middle`}
                        style={
                          stripe
                            ? { boxShadow: `inset 4px 0 0 ${stripe}` }
                            : undefined
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hasContainers ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(order._id)}
                            className={`rounded-md border p-0.5 transition-colors ${
                              rowExpanded
                                ? "border-primary-300 bg-primary-50 text-primary-700"
                                : "border-[rgba(11,15,14,0.12)] bg-white text-secondary-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                            }`}
                            aria-expanded={rowExpanded}
                            aria-label={t("list.toggleLines")}
                            title={t("list.toggleLines")}
                          >
                            {rowExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        ) : (
                          <span className="inline-block w-5" />
                        )}
                      </td>

                      {visibleColumns.map((column) => (
                        <Fragment key={column.id}>
                          {renderCell(column, order)}
                        </Fragment>
                      ))}
                    </tr>

                    {rowExpanded && (
                      <tr>
                        <td colSpan={fullSpan} className="bg-bone/60 px-3 pb-3">
                          <OrderLinesPanel
                            orderId={order._id}
                            // For the row-level CMR download: the order's
                            // direction decides which leg of a container's run
                            // the note describes.
                            order={order}
                            refreshToken={refreshTokens[order._id] ?? 0}
                            search={rowSearch}
                            // The row may be on screen because one of its
                            // containers is this handler's; the panel shows
                            // which, rather than burying it in the batch.
                            handlerScope={handlerParam}
                            onGenerate={() => setGenerateFor(order)}
                            canGenerate={order.status !== "cancelled"}
                            // Adding or removing a container changes its order's
                            // derived status and count, both of which this row
                            // shows.
                            onBatchChanged={refreshList}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <Pagination
          pagination={paginationDetails}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        />
      )}

      {generateFor && (
        <GenerateLinesModal
          isOpen
          onClose={() => setGenerateFor(null)}
          onGenerate={handleGenerate}
          generating={generating}
          orderNumber={generateFor.orderNumber}
          // The high-water mark, not the live count: positions are never
          // reused, so the preview must continue past any removed container.
          existingCount={generateFor.lineCount ?? 0}
        />
      )}
      {confirmDialog}
    </div>
  );
}
